import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Possible states for a background job */
export type JobState = 'processing' | 'completed' | 'failed';

/** A registered background job that the status bar tracks */
export interface BackgroundJob {
  /** Unique ID for this job instance */
  id: string;
  /** Human-readable job type label (e.g. 'Bulk Upload', 'Export', 'Import') */
  type: string;
  /** Short description / file name */
  label: string;
  /** Current state */
  state: JobState;
  /** Progress percentage (0-100) */
  progress: number;
  /** Current phase label (e.g. 'Validating', 'Converting') */
  phaseLabel: string;
  /** Elapsed seconds since job started */
  elapsedSeconds: number;
  /** Bootstrap icon class for the job type */
  icon: string;
  /** Timestamp when the job was registered */
  startedAt: number;
  /** Optional result message on completion */
  message?: string;
  /** Whether the user has dismissed this from the status bar */
  dismissed: boolean;
}

/**
 * Generic service for tracking background jobs across the application.
 * Any feature (bulk upload, CSV export, report generation, etc.) can
 * register a job here and the global status bar will display it.
 */
@Injectable({ providedIn: 'root' })
export class BackgroundJobsService {
  private jobs = new Map<string, BackgroundJob>();
  private jobs$ = new BehaviorSubject<BackgroundJob[]>([]);

  /** Observable list of all active (non-dismissed) jobs */
  readonly activeJobs$: Observable<BackgroundJob[]> = this.jobs$.asObservable();

  /** Current snapshot of active jobs */
  get activeJobs(): BackgroundJob[] {
    return Array.from(this.jobs.values()).filter((j) => !j.dismissed);
  }

  /** Register or update a job. If the ID exists, it's updated (merged). */
  upsertJob(job: Partial<BackgroundJob> & { id: string }): void {
    const existing = this.jobs.get(job.id);
    if (existing) {
      Object.assign(existing, job);
    } else {
      this.jobs.set(job.id, {
        type: 'Job',
        label: '',
        state: 'processing',
        progress: 0,
        phaseLabel: 'Starting...',
        elapsedSeconds: 0,
        icon: 'bi-arrow-repeat',
        startedAt: Date.now(),
        dismissed: false,
        message: undefined,
        ...job,
      });
    }
    this.emit();
  }

  /** Mark a job as completed */
  completeJob(id: string, message?: string): void {
    const job = this.jobs.get(id);
    if (job) {
      job.state = 'completed';
      job.progress = 100;
      job.message = message;
      this.emit();
    }
  }

  /** Mark a job as failed */
  failJob(id: string, message?: string): void {
    const job = this.jobs.get(id);
    if (job) {
      job.state = 'failed';
      job.message = message;
      this.emit();
    }
  }

  /** Dismiss a job from the status bar (user clicked X) */
  dismissJob(id: string): void {
    const job = this.jobs.get(id);
    if (job) {
      job.dismissed = true;
      this.emit();

      // Clean up from memory after a short delay
      setTimeout(() => {
        this.jobs.delete(id);
        this.emit();
      }, 500);
    }
  }

  /** Dismiss all completed/failed jobs */
  dismissAllDone(): void {
    for (const [id, job] of this.jobs) {
      if (job.state === 'completed' || job.state === 'failed') {
        job.dismissed = true;
      }
    }
    this.emit();
  }

  /** Remove a job entirely (no animation) */
  removeJob(id: string): void {
    this.jobs.delete(id);
    this.emit();
  }

  private emit(): void {
    this.jobs$.next(this.activeJobs);
  }
}
